import type { PropertyData } from '@strata-monorepo/shared-types'

/**
 * Returns true when a field has a usable value: truthy and not imputed.
 * Used by both the validator and the prefill function to determine whether
 * a PropertyData field should be trusted. Imputed values are treated the
 * same as missing ones — the user must supply the exact value.
 */
export const isFieldUsable = (value: unknown, imputed?: boolean | null): boolean =>
  Boolean(value) && !imputed

/**
 * Descriptor for each required home-info field.
 * Each entry carries a key (for documentation), a function to read the
 * relevant value off PropertyData, and a function to read its imputed flag.
 *
 * Both validateEnrichedCustomerPropertyData and prefillHomeInfoFromPropertyData
 * use this list. Adding a sixth field here automatically propagates to both.
 */
export const REQUIRED_HOME_INFO_FIELDS: ReadonlyArray<{
  key: string
  getValue: (p: PropertyData) => unknown
  getImputed: (p: PropertyData) => boolean | null | undefined
}> = [
  {
    key: 'bedrooms',
    getValue: (p) => p.bedrooms,
    getImputed: (p) => p.bedroomsImputed,
  },
  {
    key: 'bathrooms',
    getValue: (p) => p.bathrooms,
    getImputed: (p) => p.bathroomsImputed,
  },
  {
    key: 'squareFootage',
    getValue: (p) => p.squareFootage,
    getImputed: (p) => p.squareFootageImputed,
  },
  {
    key: 'floorCount',
    getValue: (p) => p.features?.floorCount,
    getImputed: (p) => p.featuresImputed,
  },
  {
    key: 'yearBuilt',
    getValue: (p) => p.yearBuilt,
    getImputed: (p) => p.yearBuiltImputed,
  },
]
